import type { MenuSection } from '@1js/acui-menu/lib/components/MenuDefinition';
import type { EditorViewState } from 'owa-editor';
import type { MenuItemControlProps } from '@1js/acui-ribbon-like/lib/UISurfaces/controls/Menu';
import { createAppButton, RibbonInsertTable } from 'owa-command-ribbon';
import { InsertTableDialog } from './InsertTableDialog';
import { showModal } from 'owa-modal/lib/showModal';
import { callFormatApi } from 'owa-editor/lib/utils/callFormatApi';
import updateContentToViewState from 'owa-editor/lib/utils/updateContentToViewState';
import type {
    RibbonInsertTableProps,
    AppInsertTableOnExecuteParameter,
} from '@1js/acui-ribbon-like/lib/UISurfaces/Ribbon';
import { getAppColors } from 'owa-acui-theme';
import { insertTable } from 'owa-locstrings/lib/strings/inserttable.locstring.json';
import loc from 'owa-localize';
import { getDefaultRibbonStyles } from 'owa-command-ribbon-styles/lib/util/getDefaultRibbonStyles';
import { isFeatureEnabled } from 'owa-feature-flags';
import type { IKeytipProps } from '@fluentui/react/lib/Keytip';
import { getMainWindowComposeViewState } from '../../../utils/getMainWindowComposeViewState';

const MAX_ROWS: number = 10;
const MAX_COLUMNS: number = 10;
const TABLE_FORMAT: string = '{0} x {1} table';

/**
 * creates table grid to choose table size
 * @param viewState EditorViewState
 * @param id Must be unique and may be used to determine enabledness and icon color
 * @param rows Maximum number of rows that can be chosen on the grid
 * @param columns Maximum number of columns that can be chosen on the grid
 */
function createInsertTableGrid(
    viewState: EditorViewState | undefined,
    id: string | number,
    rows: number,
    columns: number
): RibbonInsertTableProps {
    return {
        type: RibbonInsertTable,
        id: id.toString(),
        title: loc(insertTable),
        onPreviewTitle: TABLE_FORMAT,
        columns: columns,
        rows: rows,
        appColors: getAppColors(),
        onExecute: (parameter: AppInsertTableOnExecuteParameter) => {
            const editorViewState: EditorViewState | undefined =
                viewState ?? getMainWindowComposeViewState();

            if (editorViewState) {
                const { columns, rows } = parameter;
                callFormatApi(editorViewState, 'insertTable', [columns, rows], [columns, rows]);
                updateContentToViewState(editorViewState);
            }
        },
    };
}

export interface TableFlyoutSections {
    menuSections: MenuSection[];
}

export function getTableSections(
    id: string | number,
    viewState: EditorViewState | undefined,
    targetWindow: Window,
    controlLabel: string,
    iconName: string | undefined,
    tooltip: string | undefined,
    keytip: IKeytipProps | undefined,
    isButtonDisabled: boolean
): TableFlyoutSections {
    return {
        menuSections: getTableMenuSections(
            id,
            viewState,
            targetWindow,
            controlLabel,
            iconName,
            tooltip,
            keytip,
            isButtonDisabled
        ),
    };
}

function getTableMenuSections(
    id: string | number,
    viewState: EditorViewState | undefined,
    targetWindow: Window,
    controlLabel: string,
    iconName: string | undefined,
    tooltip: string | undefined,
    keytip: IKeytipProps | undefined,
    isButtonDisabled: boolean
): MenuSection[] {
    const sections: MenuSection[] = [];

    const onInsertTable = (cols: number, rows: number) => {
        const editorViewState: EditorViewState | undefined =
            viewState ?? getMainWindowComposeViewState();

        if (editorViewState) {
            callFormatApi(editorViewState, 'insertTable', [cols, rows], [cols, rows]);
            updateContentToViewState(editorViewState);
        }
    };
    const showInsertTableDialog = () => {
        const [showInsertTableDialogPromise] = showModal(InsertTableDialog, targetWindow);
        showInsertTableDialogPromise.then(value => {
            if (!value) {
                // If the user dismissed the modal, do nothing
                return;
            }
            const columns = parseInt(value.colStr);
            const rows = parseInt(value.rowStr);
            if (rows * columns > 0) {
                onInsertTable?.(columns, rows);
            }
        });
    };
    const sectionControls: MenuItemControlProps[] = [];

    // push the table grid control
    sectionControls.push(createInsertTableGrid(viewState, id, MAX_ROWS, MAX_COLUMNS));

    // push the table control that opens table dialog for manual table size selection
    sectionControls.push(
        createAppButton(
            id,
            controlLabel,
            showInsertTableDialog,
            keytip,
            iconName,
            undefined /* iconColor */,
            getDefaultRibbonStyles(),
            {
                customTooltip: isFeatureEnabled('mon-ribbon-tooltips') ? tooltip : undefined,
                disabled: isButtonDisabled,
            }
        )
    );
    sections.push({
        controls: sectionControls,
    });
    return sections;
}
